<template>
  <main class="flex-1 overflow-auto">
    <div class="p-6 lg:p-8">
      <!-- Back Button -->
      <div class="mb-6">
        <router-link to="/admin/fournisseur">
          <button class="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition">
            <icon name="ArrowLeft" :size="16" />
            Retour aux fournisseurs
          </button>
        </router-link>
      </div>

      <!-- Header -->
      <header class="mb-8">
        <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 class="text-3xl font-bold text-gray-800">{{ supplier?.name || 'Chargement...' }}</h1>
            <p class="text-gray-600">Détails du fournisseur</p>
          </div>
          <div class="flex gap-2">
            <button 
              @click="refreshData"
              class="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              :disabled="loading"
            >
              <icon name="RefreshCw" :size="16" :class="{'animate-spin': loading}" />
              {{ loading ? 'Chargement...' : 'Actualiser' }}
            </button>
            <button 
              @click="openEditModal"
              class="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              :disabled="loading"
            >
              <icon name="Edit" :size="16" />
              Modifier
            </button>
          </div>
        </div>
      </header>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p class="text-gray-600 mt-4">Chargement des données du fournisseur...</p>
      </div>

      <!-- Error State -->
      <div v-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div class="flex items-center gap-2 text-red-800">
          <icon name="AlertCircle" :size="20" />
          <span class="font-medium">Erreur :</span>
          <span>{{ error }}</span>
        </div>
      </div>

      <!-- Main Content -->
      <div v-if="supplier && !loading">
        <!-- Supplier Info and Stats -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <!-- Supplier Info Card -->
          <div class="bg-white rounded-xl shadow border border-gray-200">
            <div class="p-6">
              <div class="flex items-center justify-between mb-4">
                <h2 class="font-semibold text-gray-800">Informations</h2>
                <button 
                  @click="openEditModal"
                  class="p-2 hover:bg-gray-100 rounded-lg transition"
                >
                  <icon name="Edit" :size="18" class="text-gray-600" />
                </button>
              </div>

              <div class="space-y-4">
                <div class="flex items-center gap-3">
                  <div class="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center overflow-hidden border-2 border-blue-200">
                    <img 
                      v-if="supplier.logo"
                      :src="supplier.logo"
                      :alt="supplier.name"
                      class="w-full h-full object-contain p-2"
                    />
                    <div v-else class="text-center">
                      <span class="text-2xl font-bold text-blue-600">{{ supplier.name?.charAt(0) || 'F' }}</span>
                    </div>
                  </div>
                  <div>
                    <p class="font-semibold text-gray-800">{{ supplier.name }}</p>
                    <div class="flex items-center gap-2 mt-1">
                      <span :class="`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(supplier.status)}`">
                        {{ supplier.status_label || getStatusLabel(supplier.status) }}
                      </span>
                      <span v-if="supplier.rating" class="flex items-center gap-1 text-yellow-600">
                        <icon name="Star" :size="12" class="fill-current" />
                        <span class="text-xs font-medium">{{ supplier.rating?.toFixed?.(1) || '0.0' }}</span>
                      </span>
                    </div>
                  </div>
                </div>

                <div class="pt-4 space-y-3 text-sm">
                  <div class="flex items-center gap-3 text-gray-500">
                    <icon name="Mail" :size="16" />
                    <a :href="`mailto:${supplier.email}`" class="hover:text-blue-600 hover:underline">
                      {{ supplier.email }}
                    </a>
                  </div>
                  <div v-if="supplier.phone" class="flex items-center gap-3 text-gray-500">
                    <icon name="Phone" :size="16" />
                    <a :href="`tel:${supplier.phone}`" class="hover:text-blue-600 hover:underline">
                      {{ supplier.phone }}
                    </a>
                  </div>
                  <div v-if="supplier.website" class="flex items-center gap-3 text-gray-500">
                    <icon name="Globe" :size="16" />
                    <a :href="supplier.website" target="_blank" class="hover:text-blue-600 hover:underline truncate">
                      {{ supplier.website }}
                    </a>
                  </div>
                  <div v-if="supplier.address" class="flex items-start gap-3 text-gray-500">
                    <icon name="MapPin" :size="16" class="mt-0.5" />
                    <span class="flex-1">{{ supplier.address }}</span>
                  </div>
                  <div v-if="supplier.contact_person" class="flex items-center gap-3 text-gray-500">
                    <icon name="User" :size="16" />
                    <span>Contact : {{ supplier.contact_person }}</span>
                  </div>
                </div>

                <div v-if="supplier.contract_start_date || supplier.contract_end_date" 
                     class="pt-4 border-t border-gray-200">
                  <div class="flex items-center justify-between text-sm">
                    <span class="text-gray-500">Contrat :</span>
                    <span class="font-medium text-gray-800">
                      {{ supplier.contract_start_date ? formatDate(supplier.contract_start_date) : 'Non défini' }}
                      -
                      {{ supplier.contract_end_date ? formatDate(supplier.contract_end_date) : 'Non défini' }}
                    </span>
                  </div>
                </div>

                <div v-if="supplier.notes" class="pt-4 border-t border-gray-200">
                  <p class="text-sm text-gray-500 mb-1">Notes :</p>
                  <p class="text-sm text-gray-700 italic">{{ supplier.notes }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Stats Cards -->
          <div class="bg-white rounded-xl shadow border border-gray-200 lg:col-span-2">
            <div class="p-6">
              <h2 class="font-semibold text-gray-800 mb-4">Statistiques</h2>
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div class="p-4 bg-gray-50 rounded-xl text-center">
                  <icon name="Package" :size="24" class="mx-auto mb-2 text-blue-600" />
                  <p class="text-2xl font-bold text-gray-800">{{ supplier.product_count || 0 }}</p>
                  <p class="text-xs text-gray-500">Produits</p>
                </div>
                <div class="p-4 bg-gray-50 rounded-xl text-center">
                  <icon name="ShoppingCart" :size="24" class="mx-auto mb-2 text-blue-600" />
                  <p class="text-2xl font-bold text-gray-800">{{ supplier.order_count || 0 }}</p>
                  <p class="text-xs text-gray-500">Commandes</p>
                </div>
                <div class="p-4 bg-gray-50 rounded-xl text-center">
                  <icon name="TrendingUp" :size="24" class="mx-auto mb-2 text-green-600" />
                  <p class="text-xl font-bold text-gray-800">{{ formatPrice(supplier.total_revenue || 0) }}</p>
                  <p class="text-xs text-gray-500">CA Total</p>
                </div>
                <div class="p-4 bg-gray-50 rounded-xl text-center">
                  <icon name="Clock" :size="24" class="mx-auto mb-2 text-purple-600" />
                  <p class="text-xl font-bold text-gray-800">{{ supplier.lead_time || 7 }} jours</p>
                  <p class="text-xs text-gray-500">Délai livraison</p>
                </div>
              </div>

              <!-- Additional Financial Info -->
              <div class="mt-6 pt-6 border-t border-gray-200">
                <h3 class="font-medium text-gray-700 mb-3">Performances financières</h3>
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div class="text-center">
                    <p class="text-lg font-bold text-green-600">{{ formatPrice(totalRevenue) }}</p>
                    <p class="text-xs text-gray-500">Chiffre d'affaires</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-blue-600">{{ formatPrice(averageOrderValue) }}</p>
                    <p class="text-xs text-gray-500">Panier moyen</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-orange-600">{{ formatPrice(pendingRevenue) }}</p>
                    <p class="text-xs text-gray-500">En attente</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-purple-600">{{ formatPrice(averageProductPrice) }}</p>
                    <p class="text-xs text-gray-500">Prix moyen</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Tabs for Products and Order Items -->
        <div class="mb-6">
          <div class="border-b border-gray-200">
            <nav class="flex space-x-8">
              <button
                @click="activeTab = 'products'"
                :class="[
                  'py-2 px-1 border-b-2 font-medium text-sm transition-all',
                  activeTab === 'products'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                ]"
              >
                <span class="flex items-center gap-2">
                  <icon name="Package" :size="16" />
                  Produits
                  <span class="bg-gray-100 text-gray-800 text-xs px-2 py-0.5 rounded-full">
                    {{ products.length }}
                  </span>
                </span>
              </button>
              <button
                @click="activeTab = 'order_items'"
                :class="[
                  'py-2 px-1 border-b-2 font-medium text-sm transition-all',
                  activeTab === 'order_items'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                ]"
              >
                <span class="flex items-center gap-2">
                  <icon name="ShoppingCart" :size="16" />
                  Items commandés
                  <span class="bg-gray-100 text-gray-800 text-xs px-2 py-0.5 rounded-full">
                    {{ orderItems.length }}
                  </span>
                </span>
              </button>
              <button
                @click="activeTab = 'performance'"
                :class="[
                  'py-2 px-1 border-b-2 font-medium text-sm transition-all',
                  activeTab === 'performance'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                ]"
              >
                <span class="flex items-center gap-2">
                  <icon name="BarChart" :size="16" />
                  Performance
                </span>
              </button>
            </nav>
          </div>
        </div>

        <!-- Products Tab -->
        <div v-if="activeTab === 'products'">
          <div class="bg-white rounded-xl shadow border border-gray-200 mb-6">
            <div class="p-6">
              <div class="flex items-center justify-between mb-4">
                <h2 class="font-semibold text-gray-800 flex items-center gap-2">
                  <icon name="Package" :size="20" /> 
                  Produits ({{ products.length }})
                </h2>
                <div class="relative">
                  <icon name="Search" class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    v-model="productSearch"
                    placeholder="Rechercher un produit..."
                    class="pl-10 w-64 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Produit</th>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Catégorie</th>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Prix</th>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Stock</th>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    <tr 
                      v-for="product in filteredProducts" 
                      :key="product.id"
                      class="hover:bg-gray-50 transition-colors"
                    >
                      <td class="p-3">
                        <div class="flex items-center gap-3">
                          <div class="w-10 h-10 rounded bg-gray-100 flex items-center justify-center overflow-hidden">
                            <img 
                              v-if="product.cover_image"
                              :src="this.$store.state.baseUrl+product.cover_image"
                              :alt="product.name"
                              class="w-full h-full object-cover"
                            />
                            <icon name="Package" :size="20" class="text-gray-400" v-else />
                          </div>
                          <div>
                            <p class="font-medium text-gray-800 text-sm">{{ product.name }}</p>
                            <p class="text-xs text-gray-500">Ref: {{ product.sku || product.id }}</p>
                          </div>
                        </div>
                      </td>
                      <td class="p-3 text-sm text-gray-700">
                        {{ product.categorie?.name || 'Non catégorisé' }}
                      </td>
                      <td class="p-3">
                        <div>
                          <p class="font-semibold text-gray-800 text-sm">{{ formatPrice(product.price) }}</p>
                          <p v-if="product.discounted_price" class="text-xs text-green-600">
                            Promo: {{ formatPrice(product.discounted_price) }}
                          </p>
                        </div>
                      </td>
                      <td class="p-3">
                        <div class="flex items-center gap-2">
                          <div class="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              :class="[
                                'h-full rounded-full',
                                product.stock_quantity > 10 ? 'bg-green-500' :
                                product.stock_quantity > 0 ? 'bg-yellow-500' : 'bg-red-500'
                              ]"
                              :style="{ width: `${Math.min((product.stock_quantity / 50) * 100, 100)}%` }"
                            ></div>
                          </div>
                          <span class="text-xs font-medium text-gray-700 min-w-8 text-right">
                            {{ product.stock_quantity || 0 }}
                          </span>
                        </div>
                      </td>
                      <td class="p-3">
                        <span :class="[
                          'px-2 py-1 rounded-full text-xs font-medium',
                          product.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        ]">
                          {{ product.is_active ? 'Actif' : 'Inactif' }}
                        </span>
                      </td>
                      <td class="p-3">
                        <div class="flex items-center gap-1">
                          <router-link :to="`/admin/produits/${product.id}`">
                            <button class="p-1.5 hover:bg-gray-100 rounded transition" title="Voir">
                              <icon name="Eye" :size="14" class="text-gray-600" />
                            </button>
                          </router-link>
                          <button 
                            @click="copyProductId(product.id)"
                            class="p-1.5 hover:bg-gray-100 rounded transition" 
                            title="Copier ID"
                          >
                            <icon name="Copy" :size="14" class="text-gray-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Empty State -->
              <div v-if="filteredProducts.length === 0" class="text-center py-8">
                <div class="w-16 h-16 mx-auto mb-4 text-gray-400">
                  <icon name="Package" :size="64" />
                </div>
                <p class="text-gray-600 mb-2">Aucun produit trouvé</p>
                <p class="text-sm text-gray-500">
                  {{ productSearch ? 'Aucun résultat pour votre recherche' : 'Ce fournisseur n\'a pas encore de produits' }}
                </p>
              </div>

              <!-- Product Stats -->
              <div v-if="products.length > 0" class="mt-6 pt-6 border-t border-gray-200">
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div class="text-center">
                    <p class="text-lg font-bold text-gray-800">{{ products.length }}</p>
                    <p class="text-xs text-gray-500">Total produits</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-green-600">{{ activeProductsCount }}</p>
                    <p class="text-xs text-gray-500">Produits actifs</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-red-600">{{ outOfStockCount }}</p>
                    <p class="text-xs text-gray-500">En rupture</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-blue-600">{{ formatPrice(productsValue) }}</p>
                    <p class="text-xs text-gray-500">Valeur stock</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Items Tab -->
        <div v-if="activeTab === 'order_items'">
          <div class="bg-white rounded-xl shadow border border-gray-200 mb-6">
            <div class="p-6">
              <div class="flex items-center justify-between mb-4">
                <h2 class="font-semibold text-gray-800 flex items-center gap-2">
                  <icon name="ShoppingCart" :size="20" /> 
                  Items commandés ({{ orderItems.length }})
                </h2>
                <div class="relative">
                  <icon name="Search" class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    v-model="orderItemSearch"
                    placeholder="Rechercher un item..."
                    class="pl-10 w-64 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div class="overflow-x-auto">
                <table class="w-full">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Commande</th>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Produit</th>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Qté</th>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Prix unitaire</th>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Statut commande</th>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th class="text-left p-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-200">
                    <tr 
                      v-for="item in filteredOrderItems" 
                      :key="item.id"
                      class="hover:bg-gray-50 transition-colors"
                    >
                      <td class="p-3">
                        <div>
                          <p class="font-medium text-gray-800 text-sm">{{ item.order?.order_number || item.order_number }}</p>
                          <p v-if="item.order?.customer_name" class="text-xs text-gray-500">
                            {{ item.order.customer_name }}
                          </p>
                        </div>
                      </td>
                      <td class="p-3">
                        <div class="flex items-center gap-2">
                          <div class="w-8 h-8 rounded bg-gray-100 flex items-center justify-center overflow-hidden">
                            <img 
                              v-if="item.product?.cover_image"
                              :src="$store.state.baseUrl+item.product.cover_image"
                              :alt="item.product_name"
                              class="w-full h-full object-cover"
                            />
                            <icon name="Package" :size="14" class="text-gray-400" v-else />
                          </div>
                          <div>
                            <p class="font-medium text-gray-800 text-sm">{{ item.product_name }}</p>
                            <p v-if="item.size || item.color" class="text-xs text-gray-500">
                              {{ item.size }} {{ item.color ? `• ${item.color}` : '' }}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td class="p-3 text-sm text-gray-700 text-center">
                        {{ item.quantity }}
                      </td>
                      <td class="p-3">
                        <p class="font-semibold text-gray-800 text-sm">{{ formatPrice(item.price) }}</p>
                      </td>
                      <td class="p-3">
                        <p class="font-bold text-gray-800 text-sm">{{ formatPrice(item.total) }}</p>
                      </td>
                      <td class="p-3">
                        <span :class="`px-2 py-1 rounded-full text-xs font-medium ${getOrderStatusColor(item.order?.status)}`">
                          {{ getOrderStatusLabel(item.order?.status) }}
                        </span>
                      </td>
                      <td class="p-3 text-sm text-gray-700">
                        {{ item.created_at ? formatDate(item.created_at) : 'N/A' }}
                      </td>
                      <td class="p-3">
                        <div class="flex items-center gap-1">
                          <button 
                            @click="openOrderDetails(item.order)"
                            class="p-1.5 hover:bg-gray-100 rounded transition" 
                            title="Voir commande"
                          >
                            <icon name="Eye" :size="14" class="text-gray-600" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Empty State -->
              <div v-if="filteredOrderItems.length === 0" class="text-center py-8">
                <div class="w-16 h-16 mx-auto mb-4 text-gray-400">
                  <icon name="ShoppingCart" :size="64" />
                </div>
                <p class="text-gray-600 mb-2">Aucun item commandé trouvé</p>
                <p class="text-sm text-gray-500">
                  {{ orderItemSearch ? 'Aucun résultat pour votre recherche' : 'Ce fournisseur n\'a pas encore d\'items commandés' }}
                </p>
              </div>

              <!-- Order Items Stats -->
              <div v-if="orderItems.length > 0" class="mt-6 pt-6 border-t border-gray-200">
                <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div class="text-center">
                    <p class="text-lg font-bold text-gray-800">{{ uniqueOrdersCount }}</p>
                    <p class="text-xs text-gray-500">Commandes uniques</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-blue-600">{{ totalItemsQuantity }}</p>
                    <p class="text-xs text-gray-500">Quantité totale</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-green-600">{{ formatPrice(totalRevenue) }}</p>
                    <p class="text-xs text-gray-500">Revenus totaux</p>
                  </div>
                  <div class="text-center">
                    <p class="text-lg font-bold text-purple-600">{{ formatPrice(averageItemValue) }}</p>
                    <p class="text-xs text-gray-500">Valeur moyenne/item</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Performance Tab -->
        <div v-if="activeTab === 'performance'">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <!-- Performance Metrics -->
            <div class="bg-white rounded-xl shadow border border-gray-200 p-6">
              <h2 class="font-semibold text-gray-800 mb-4">Métriques de performance</h2>
              <div class="space-y-4">
                <div>
                  <div class="flex justify-between mb-1">
                    <span class="text-sm text-gray-600">Taux de vente produits</span>
                    <span class="text-sm font-medium text-gray-800">
                      {{ products.length > 0 ? Math.round((orderItems.length / products.length) * 100) : 0 }}%
                    </span>
                  </div>
                  <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      class="h-full rounded-full bg-green-500 transition-all duration-500"
                      :style="{ width: `${Math.min((orderItems.length / Math.max(products.length, 1)) * 100, 100)}%` }"
                    ></div>
                  </div>
                </div>
                
                <div>
                  <div class="flex justify-between mb-1">
                    <span class="text-sm text-gray-600">Satisfaction client</span>
                    <span class="text-sm font-medium text-gray-800">
                      {{ supplier.rating?.toFixed?.(1) || 0 }}/5
                    </span>
                  </div>
                  <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      class="h-full rounded-full bg-yellow-500 transition-all duration-500"
                      :style="{ width: `${((supplier.rating || 0) / 5) * 100}%` }"
                    ></div>
                  </div>
                </div>
                
                <div>
                  <div class="flex justify-between mb-1">
                    <span class="text-sm text-gray-600">Taux de remplissage stock</span>
                    <span class="text-sm font-medium text-gray-800">
                      {{ stockFillRate }}%
                    </span>
                  </div>
                  <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      class="h-full rounded-full bg-blue-500 transition-all duration-500"
                      :style="{ width: `${stockFillRate}%` }"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Financial Overview -->
            <div class="bg-white rounded-xl shadow border border-gray-200 p-6">
              <h2 class="font-semibold text-gray-800 mb-4">Aperçu financier</h2>
              <div class="space-y-4">
                <div class="flex justify-between items-center">
                  <span class="text-sm text-gray-600">Chiffre d'affaires total</span>
                  <span class="font-medium text-green-600">{{ formatPrice(totalRevenue) }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-gray-600">Revenus en attente</span>
                  <span class="font-medium text-orange-600">{{ formatPrice(pendingRevenue) }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-gray-600">Revenus payés</span>
                  <span class="font-medium text-blue-600">{{ formatPrice(paidRevenue) }}</span>
                </div>
                <div class="flex justify-between items-center">
                  <span class="text-sm text-gray-600">Panier moyen</span>
                  <span class="font-medium text-purple-600">{{ formatPrice(averageOrderValue) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Recent Activity -->
          <div class="bg-white rounded-xl shadow border border-gray-200 p-6 mt-6">
            <h2 class="font-semibold text-gray-800 mb-4">Activité récente</h2>
            <div class="space-y-3">
              <div v-if="orderItems.length > 0" class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div class="flex items-center gap-3">
                  <div class="p-2 bg-green-100 rounded-lg">
                    <icon name="ShoppingCart" :size="16" class="text-green-600" />
                  </div>
                  <div>
                    <p class="font-medium text-gray-800">Dernière commande</p>
                    <p class="text-sm text-gray-500">
                      {{ orderItems[0]?.order?.order_number || 'N/A' }} - {{ formatPrice(orderItems[0]?.total || 0) }}
                    </p>
                  </div>
                </div>
                <span class="text-sm text-gray-500">{{ orderItems[0]?.created_at ? formatDate(orderItems[0].created_at) : 'N/A' }}</span>
              </div>
              
              <div v-if="products.length > 0" class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div class="flex items-center gap-3">
                  <div class="p-2 bg-blue-100 rounded-lg">
                    <icon name="Package" :size="16" class="text-blue-600" />
                  </div>
                  <div>
                    <p class="font-medium text-gray-800">Dernier produit ajouté</p>
                    <p class="text-sm text-gray-500">{{ products[0]?.name || 'N/A' }}</p>
                  </div>
                </div>
                <span class="text-sm text-gray-500">{{ products[0]?.created_at ? formatDate(products[0].created_at) : 'N/A' }}</span>
              </div>
              
              <div class="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div class="flex items-center gap-3">
                  <div class="p-2 bg-yellow-100 rounded-lg">
                    <icon name="Calendar" :size="16" class="text-yellow-600" />
                  </div>
                  <div>
                    <p class="font-medium text-gray-800">Dernière mise à jour</p>
                    <p class="text-sm text-gray-500">Informations du fournisseur</p>
                  </div>
                </div>
                <span class="text-sm text-gray-500">{{ supplier.updated_at ? formatDate(supplier.updated_at) : 'N/A' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Supplier Modal -->
    <div
      v-if="showEditModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      @click.self="closeEditModal"
    >
      <div class="bg-white rounded-xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div class="p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-gray-800">Modifier le fournisseur</h3>
            <button
              @click="closeEditModal"
              class="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <icon name="X" :size="20" class="text-gray-500" />
            </button>
          </div>

          <!-- Edit Form -->
          <form @submit.prevent="handleSaveSupplier" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Logo -->
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Logo
                </label>
                <div class="flex items-center gap-4">
                  <div class="w-20 h-20 rounded-lg bg-gray-100 border border-gray-300 flex items-center justify-center overflow-hidden">
                    <img
                      v-if="editForm.logoPreview"
                      :src="editForm.logoPreview"
                      alt="Logo preview"
                      class="w-full h-full object-contain p-2"
                    />
                    <div v-else class="text-center">
                      <icon name="Building2" :size="20" class="text-gray-400 mx-auto mb-1" />
                      <p class="text-xs text-gray-500">Logo</p>
                    </div>
                  </div>
                  <div class="flex-1">
                    <input
                      type="file"
                      ref="logoInput"
                      @change="handleLogoUpload"
                      accept="image/*"
                      class="hidden"
                    />
                    <button
                      type="button"
                      @click="$refs.logoInput.click()"
                      class="border border-gray-300 bg-white hover:bg-gray-50 px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1"
                    >
                      <icon name="Upload" :size="14" />
                      Télécharger
                    </button>
                    <p class="text-xs text-gray-500 mt-1">
                      PNG, JPG, GIF jusqu'à 2MB
                    </p>
                  </div>
                </div>
              </div>

              <!-- Informations de base -->
              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Nom du fournisseur *
                </label>
                <input
                  v-model="editForm.name"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  v-model="editForm.email"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone *
                </label>
                <input
                  v-model="editForm.phone"
                  required
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Personne de contact
                </label>
                <input
                  v-model="editForm.contact_person"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Site web
                </label>
                <input
                  type="url"
                  v-model="editForm.website"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Adresse
                </label>
                <textarea
                  v-model="editForm.address"
                  rows="2"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                ></textarea>
              </div>

              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  v-model="editForm.notes"
                  rows="3"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                ></textarea>
              </div>

              <!-- Informations supplémentaires -->
              <div class="md:col-span-2">
                <h4 class="font-medium text-gray-700 mb-3">Informations supplémentaires</h4>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Numéro fiscal
                </label>
                <input
                  v-model="editForm.tax_number"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Compte bancaire
                </label>
                <input
                  v-model="editForm.bank_account"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Conditions de paiement
                </label>
                <input
                  v-model="editForm.payment_terms"
                  placeholder="30 jours net"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Délai de livraison (jours)
                </label>
                <input
                  type="number"
                  v-model="editForm.lead_time"
                  min="1"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Commande minimale (FCFA)
                </label>
                <input
                  type="number"
                  v-model="editForm.minimum_order_value"
                  min="0"
                  step="0.01"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Note (0-5)
                </label>
                <input
                  type="number"
                  v-model="editForm.rating"
                  min="0"
                  max="5"
                  step="0.1"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <!-- Dates de contrat -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Début du contrat
                </label>
                <input
                  type="date"
                  v-model="editForm.contract_start_date"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Fin du contrat
                </label>
                <input
                  type="date"
                  v-model="editForm.contract_end_date"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <!-- Statut -->
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Statut
                </label>
                <select
                  v-model="editForm.status"
                  class="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="active">Actif</option>
                  <option value="inactive">Inactif</option>
                </select>
              </div>
            </div>

            <!-- Actions -->
            <div class="flex space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                @click="closeEditModal"
                class="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Annuler
              </button>
              <button
                type="submit"
                :disabled="saving"
                class="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="saving">
                  <icon name="Loader" class="animate-spin inline-block mr-2" :size="16" />
                  Enregistrement...
                </span>
                <span v-else>Enregistrer</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Order Details Modal -->
    <div
      v-if="selectedOrder"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      @click.self="selectedOrder = null"
    >
      <div class="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div class="p-6">
          <div class="flex items-center justify-between mb-6">
            <div>
              <h3 class="text-lg font-semibold text-gray-800">Commande {{ selectedOrder.order_number }}</h3>
              <p class="text-sm text-gray-500">
                {{ formatDateFull(selectedOrder.created_at) }}
              </p>
            </div>
            <button 
              @click="selectedOrder = null"
              class="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <icon name="X" :size="20" class="text-gray-500" />
            </button>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <!-- Informations client -->
            <div class="space-y-4">
              <h4 class="font-medium text-gray-700 flex items-center gap-2">
                <icon name="User" :size="16" /> Informations client
              </h4>
              <div class="p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
                <p class="font-medium text-gray-900">{{ selectedOrder.customer_name }}</p>
                <p class="text-gray-700">{{ selectedOrder.customer_email || selectedOrder.user?.email }}</p>
                <p class="text-gray-700 flex items-center gap-2">
                  <icon name="Phone" :size="14" /> {{ selectedOrder.customer_phone }}
                </p>
              </div>
            </div>

            <!-- Adresse de livraison -->
            <div class="space-y-4">
              <h4 class="font-medium text-gray-700 flex items-center gap-2">
                <icon name="MapPin" :size="16" /> Adresse de livraison
              </h4>
              <div class="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 whitespace-pre-line">
                {{ selectedOrder.shipping_address }}
              </div>
            </div>

            <!-- Informations de commande -->
            <div class="space-y-4">
              <h4 class="font-medium text-gray-700 flex items-center gap-2">
                <icon name="CreditCard" :size="16" /> Paiement & Livraison
              </h4>
              <div class="p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-600">Mode de paiement:</span>
                  <span class="font-medium">{{ formatPaymentMethod(selectedOrder.payment_method) }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">Statut paiement:</span>
                  <span :class="`px-2 py-0.5 rounded text-xs font-medium ${getPaymentStatusColor(selectedOrder.payment_status)}`">
                    {{ getPaymentStatusLabel(selectedOrder.payment_status) }}
                  </span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-600">Frais de livraison:</span>
                  <span class="font-medium">{{ formatPrice(selectedOrder.shipping || 0) }}</span>
                </div>
              </div>
            </div>

            <!-- Statut de commande -->
            <div class="space-y-4">
              <h4 class="font-medium text-gray-700 flex items-center gap-2">
                <icon name="Package" :size="16" /> Statut de commande
              </h4>
              <div class="p-4 bg-gray-50 rounded-lg space-y-2 text-sm">
                <div class="flex items-center justify-between">
                  <span class="text-gray-600">Statut:</span>
                  <span :class="`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedOrder.status)}`">
                    {{ selectedOrder.status_text || getStatusLabel(selectedOrder.status) }}
                  </span>
                </div>
                
                <div v-if="selectedOrder.notes" class="mt-4">
                  <p class="text-gray-600 mb-1">Notes:</p>
                  <p class="text-gray-700 italic">"{{ selectedOrder.notes }}"</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Articles commandés -->
          <div class="space-y-4">
            <h4 class="font-medium text-gray-700 flex items-center gap-2">
              <icon name="Package" :size="16" /> Articles commandés
            </h4>
            <div class="border border-gray-200 rounded-lg overflow-hidden">
              <table class="w-full">
                <thead class="bg-gray-50">
                  <tr>
                    <th class="text-left p-3 text-sm font-medium text-gray-500">Produit</th>
                    <th class="text-left p-3 text-sm font-medium text-gray-500">Fournisseur</th>
                    <th class="text-center p-3 text-sm font-medium text-gray-500">Qté</th>
                    <th class="text-center p-3 text-sm font-medium text-gray-500">Taille</th>
                    <th class="text-center p-3 text-sm font-medium text-gray-500">Couleur</th>
                    <th class="text-right p-3 text-sm font-medium text-gray-500">Prix unitaire</th>
                    <th class="text-right p-3 text-sm font-medium text-gray-500">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr 
                    v-for="(item, index) in orderItemsFromSelectedOrder" 
                    :key="index"
                    class="border-t border-gray-200 hover:bg-gray-50"
                  >
                    <td class="p-3 text-gray-700">
                      <div>
                        <p class="font-medium">{{ item.product_name }}</p>
                        <p v-if="item.product" class="text-xs text-gray-500">
                          Réf: {{ item.product.sku || item.product_id }}
                        </p>
                      </div>
                    </td>
                    <td class="p-3 text-gray-700">
                      <div v-if="item.supplier_id === supplier?.id" class="flex items-center gap-2">
                        <span class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Ce fournisseur</span>
                      </div>
                      <div v-else class="text-xs text-gray-500">
                        Autre fournisseur
                      </div>
                    </td>
                    <td class="p-3 text-center text-gray-700">{{ item.quantity }}</td>
                    <td class="p-3 text-center text-gray-700">{{ item.size || '—' }}</td>
                    <td class="p-3 text-center text-gray-700">
                      <span v-if="item.color" class="inline-flex items-center gap-1">
                        <span 
                          v-if="item.color.startsWith('#')"
                          class="w-3 h-3 rounded-full border border-gray-300"
                          :style="{ backgroundColor: item.color }"
                        ></span>
                        {{ item.color }}
                      </span>
                      <span v-else>—</span>
                    </td>
                    <td class="p-3 text-right text-gray-700">{{ formatPrice(item.price) }}</td>
                    <td class="p-3 text-right text-gray-700 font-medium">
                      {{ formatPrice(item.total) }}
                    </td>
                  </tr>
                </tbody>
                <tfoot class="bg-gray-100">
                  <tr class="border-t border-gray-200">
                    <td colspan="6" class="p-3 text-right font-medium text-gray-700">Sous-total</td>
                    <td class="p-3 text-right font-medium text-gray-700">
                      {{ formatPrice(selectedOrder.subtotal) }}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="6" class="p-3 text-right font-medium text-gray-700">Livraison</td>
                    <td class="p-3 text-right font-medium text-gray-700">
                      {{ formatPrice(selectedOrder.shipping || 0) }}
                    </td>
                  </tr>
                  <tr class="border-t border-gray-300">
                    <td colspan="6" class="p-3 text-right font-bold text-gray-900">Total</td>
                    <td class="p-3 text-right font-bold text-lg text-gray-900">
                      {{ formatPrice(selectedOrder.total) }}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <div class="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <div class="flex items-center space-x-4">
              <span :class="`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedOrder.status)}`">
                {{ selectedOrder.status_text || getStatusLabel(selectedOrder.status) }}
              </span>
              <span :class="`px-3 py-1 rounded-full text-sm font-medium ${getPaymentStatusColor(selectedOrder.payment_status)}`">
                {{ getPaymentStatusLabel(selectedOrder.payment_status) }}
              </span>
            </div>
            <div class="flex items-center space-x-2">
              <button 
                @click="printOrder"
                class="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
              >
                <icon name="Printer" :size="16" /> Imprimer
              </button>
              <button 
                @click="selectedOrder = null"
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { useRoute } from 'vue-router'
import axios from 'axios'

export default {
  name: 'SupplierDetailPage',

  setup() {
    const route = useRoute()
    return { supplierId: route.params.id }
  },

  data() {
    return {
      activeTab: 'products',
      loading: true,
      error: null,
      showEditModal: false,
      selectedOrder: null,
      productSearch: '',
      orderItemSearch: '',
      saving: false,
      
      // Supplier details
      supplier: null,
      products: [],
      orderItems: [],
      
      // Edit form
      editForm: {
        id: '',
        name: '',
        email: '',
        phone: '',
        address: '',
        contact_person: '',
        website: '',
        logo: null,
        logoPreview: null,
        notes: '',
        status: 'active',
        payment_terms: '',
        tax_number: '',
        bank_account: '',
        contract_start_date: '',
        contract_end_date: '',
        rating: 0,
        lead_time: 7,
        minimum_order_value: 0
      }
    }
  },

  computed: {
    ...mapState({
      adminSuppliers: state => state.adminSuppliers,
      adminProducts: state => state.admin.products,
      adminOrders: state => state.admin.orders
    }),
    
    filteredProducts() {
      if (!this.products) return []
      return this.products.filter(product => 
        product.name?.toLowerCase().includes(this.productSearch.toLowerCase()) ||
        product.sku?.toLowerCase().includes(this.productSearch.toLowerCase()) ||
        product.categorie?.name?.toLowerCase().includes(this.productSearch.toLowerCase())
      )
    },
    
    filteredOrderItems() {
      if (!this.orderItems) return []
      return this.orderItems.filter(item => 
        item.product_name?.toLowerCase().includes(this.orderItemSearch.toLowerCase()) ||
        item.order?.order_number?.toLowerCase().includes(this.orderItemSearch.toLowerCase()) ||
        item.order?.customer_name?.toLowerCase().includes(this.orderItemSearch.toLowerCase())
      )
    },
    
    // Product statistics
    activeProductsCount() {
      return this.products.filter(p => p.is_active).length
    },
    
    outOfStockCount() {
      return this.products.filter(p => p.stock_quantity <= 0).length
    },
    
    productsValue() {
      return this.products.reduce((total, p) => total + (p.price * (p.stock_quantity || 0)), 0)
    },
    
    averageProductPrice() {
      return this.products.length > 0 
        ? this.products.reduce((sum, p) => sum + p.price, 0) / this.products.length 
        : 0
    },
    
    stockFillRate() {
      if (this.products.length === 0) return 0
      const totalStock = this.products.reduce((sum, p) => sum + (p.stock_quantity || 0), 0)
      const maxStock = this.products.length * 50 // Assuming max stock of 50 per product
      return Math.round((totalStock / maxStock) * 100)
    },
    
    // Order items statistics
    uniqueOrdersCount() {
      if (!this.orderItems.length) return 0
      const orderIds = new Set(this.orderItems.map(item => item.order_id))
      return orderIds.size
    },
    
    totalItemsQuantity() {
      return this.orderItems.reduce((sum, item) => sum + (item.quantity || 0), 0)
    },
    
    totalRevenue() {
      return this.orderItems.reduce((sum, item) => sum + (item.total || 0), 0)
    },
    
    pendingRevenue() {
      return this.orderItems
        .filter(item => item.order?.payment_status === 1) // Pending payment
        .reduce((sum, item) => sum + (item.total || 0), 0)
    },
    
    paidRevenue() {
      return this.orderItems
        .filter(item => item.order?.payment_status === 2) // Paid
        .reduce((sum, item) => sum + (item.total || 0), 0)
    },
    
    averageOrderValue() {
      const paidItems = this.orderItems.filter(item => item.order?.payment_status === 2)
      if (paidItems.length === 0) return 0
      
      const totalPaid = paidItems.reduce((sum, item) => sum + (item.total || 0), 0)
      const uniquePaidOrders = new Set(paidItems.map(item => item.order_id))
      return totalPaid / uniquePaidOrders.size
    },
    
    averageItemValue() {
      return this.orderItems.length > 0 
        ? this.totalRevenue / this.orderItems.length 
        : 0
    },
    
    orderItemsFromSelectedOrder() {
      if (!this.selectedOrder || !this.selectedOrder.items) return []
      return this.selectedOrder.items
    }
  },

  methods: {
    ...mapActions([
      'fetchAdminSuppliers',
      'fetchAdminData',
      'saveSupplier'
    ]),
    
    formatPrice(price) {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XAF'
      }).format(price)
    },
    
    formatDate(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR')
    },
    
    formatDateFull(dateString) {
      if (!dateString) return 'N/A'
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    
    getStatusColor(status) {
      return status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
    },
    
    getStatusLabel(status) {
      return status === 'active' ? 'Actif' : 'Inactif'
    },
    
    getOrderStatusColor(status) {
      switch (parseInt(status)) {
        case 4: return 'bg-green-100 text-green-800'
        case 3: return 'bg-blue-100 text-blue-800'
        case 2: return 'bg-purple-100 text-purple-800'
        case 1: return 'bg-yellow-100 text-yellow-800'
        case 0: return 'bg-red-100 text-red-800'
        default: return 'bg-gray-100 text-gray-800'
      }
    },
    
    getOrderStatusLabel(status) {
      const labels = {
        0: 'Rejeté',
        1: 'En cours',
        2: 'Accepté',
        3: 'Expédié',
        4: 'Livré'
      }
      return labels[parseInt(status)] || `Statut ${status}`
    },
    
    getPaymentStatusColor(status) {
      switch (parseInt(status)) {
        case 2: return 'bg-green-100 text-green-800'
        case 1: return 'bg-yellow-100 text-yellow-800'
        case 3: return 'bg-red-100 text-red-800'
        default: return 'bg-gray-100 text-gray-800'
      }
    },
    
    getPaymentStatusLabel(status) {
      const labels = {
        1: 'En attente',
        2: 'Payé',
        3: 'Échoué'
      }
      return labels[parseInt(status)] || `Paiement ${status}`
    },
    
    formatPaymentMethod(method) {
      const methods = {
        'card': 'Carte bancaire',
        'mobile_money': 'Mobile Money',
        'campay': 'Campay',
        'cash': 'Paiement à la livraison'
      }
      return methods[method] || method || 'Non spécifié'
    },
    
    async loadData() {
      try {
        this.loading = true
        this.error = null
        
        // Load supplier details from API
        const token = localStorage.getItem('token')
        const response = await axios.get(`${this.$store.state.localhost}admin/suppliers/${this.supplierId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })
        
        const data = response.data
        this.supplier = data.supplier
        this.products = data.products || []
        this.orderItems = data.orders || []
        
        // Load admin data if not already loaded for other purposes
        if (!this.adminProducts || this.adminProducts.length === 0) {
          await this.fetchAdminData()
        }
        
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error)
        this.error = error.response?.data?.message || 'Impossible de charger les données du fournisseur'
      } finally {
        this.loading = false
      }
    },
    
    async refreshData() {
      await this.loadData()
      this.$emit('notify', {
        title: 'Actualisé',
        message: 'Les données ont été actualisées',
        type: 'success'
      })
    },
    
    openEditModal() {
      if (this.supplier) {
        this.editForm = {
          id: this.supplier.id,
          name: this.supplier.name,
          email: this.supplier.email,
          phone: this.supplier.phone,
          address: this.supplier.address,
          contact_person: this.supplier.contact_person,
          website: this.supplier.website,
          logo: null,
          logoPreview: this.supplier.logo,
          notes: this.supplier.notes,
          status: this.supplier.status,
          payment_terms: this.supplier.payment_terms,
          tax_number: this.supplier.tax_number,
          bank_account: this.supplier.bank_account,
          contract_start_date: this.supplier.contract_start_date,
          contract_end_date: this.supplier.contract_end_date,
          rating: this.supplier.rating,
          lead_time: this.supplier.lead_time,
          minimum_order_value: this.supplier.minimum_order_value
        }
      }
      this.showEditModal = true
    },
    
    closeEditModal() {
      this.showEditModal = false
    },
    
    handleLogoUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.editForm.logo = file
        const reader = new FileReader()
        reader.onload = (e) => {
          this.editForm.logoPreview = e.target.result
        }
        reader.readAsDataURL(file)
      }
    },
    
    async handleSaveSupplier() {
      try {
        this.saving = true
        
        const formData = new FormData()
        
        // Add all fields
        Object.keys(this.editForm).forEach(key => {
          if (key !== 'logo' && key !== 'logoPreview' && this.editForm[key] !== null && this.editForm[key] !== undefined) {
            formData.append(key, this.editForm[key])
          }
        })
        
        // Add logo if new
        if (this.editForm.logo instanceof File) {
          formData.append('logo', this.editForm.logo)
        }
        
        await this.saveSupplier(formData)
        
        this.closeEditModal()
        await this.loadData()
        
        this.$emit('notify', {
          title: 'Succès',
          message: 'Fournisseur mis à jour avec succès',
          type: 'success'
        })
        
      } catch (error) {
        console.error('Erreur lors de la mise à jour du fournisseur:', error)
        this.$emit('notify', {
          title: 'Erreur',
          message: error.response?.data?.message || 'Erreur lors de la mise à jour',
          type: 'error'
        })
      } finally {
        this.saving = false
      }
    },
    
    openOrderDetails(order) {
      this.selectedOrder = order
    },
    
    copyProductId(id) {
      navigator.clipboard.writeText(id.toString())
      this.$emit('notify', {
        title: 'Copié',
        message: 'ID du produit copié dans le presse-papier',
        type: 'success'
      })
    },
    
    printOrder() {
      if (!this.selectedOrder) return
      
      const printContent = document.createElement('div')
      printContent.innerHTML = `
        <style>
          @media print {
            body * {
              visibility: hidden;
            }
            .printable, .printable * {
              visibility: visible;
            }
            .printable {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 20px;
            }
          }
        </style>
        <div class="printable">
          <h2>Commande ${this.selectedOrder.order_number}</h2>
          <p>Date: ${this.formatDateFull(this.selectedOrder.created_at)}</p>
          <p>Client: ${this.selectedOrder.customer_name}</p>
          <p>Téléphone: ${this.selectedOrder.customer_phone}</p>
          <p>Total: ${this.formatPrice(this.selectedOrder.total)}</p>
          <p>Statut: ${this.getStatusLabel(this.selectedOrder.status)}</p>
        </div>
      `
      
      document.body.appendChild(printContent)
      window.print()
      document.body.removeChild(printContent)
    }
  },

  async mounted() {
    await this.loadData()
  },

  watch: {
    supplierId() {
      this.loadData()
    }
  }
}
</script>

<style scoped>
/* Animation pour les transitions */
button, .hover\:bg-gray-50 {
  transition: all 0.2s ease;
}

.bg-gray-50 {
  transition: background-color 0.2s ease;
}

/* Styles pour les cartes */
.bg-white {
  transition: box-shadow 0.2s ease;
}

.bg-white:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

/* Style pour le lien de retour */
button:hover {
  transform: translateY(-1px);
}

/* Animation de spin */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Smooth transitions for progress bars */
.h-full {
  transition: width 0.5s ease;
}

/* Table row hover effect */
tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}
</style>